// src/components/SpeechAssessment.js
import React, { useState, useRef, useEffect } from "react";
import { PlayButton } from "./PlayButton";
import { CompletedScreen } from "./CompletedScreen";
import Logo from "../assets/images/commaLogo.png";
import ProgressZero from "../assets/images/progress_0.png";
import ProgressHalf from "../assets/images/progress_50.png";
import ProgressFull from "../assets/images/progress_100.png";
import ProgressComplete from "../assets/images/progress_complete.png";
import MicIdle from "../assets/images/mic_idle.png";
import MicSpeaking from "../assets/images/mic_speaking.png";
import MicActive from "../assets/images/mic_active.png";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { LinearProgress, Box, Typography } from "@mui/material";

/**
 * SpeechAssessment: plays prompts in sequence, records user with dynamic mic volume indicator, submits.
 */
export default function SpeechAssessment({
  feedID: propFeedID,
  patientID: propPatientID,
}) {
  const location = useLocation();
  // Use propFeedID and propPatientID if provided, otherwise fallback to location.state
  const feedID = propFeedID || (location.state && location.state.feedID);
  const patientID =
    propPatientID || (location.state && location.state.patientID);
  const [prompts, setPrompts] = useState([]);
  const [current, setCurrent] = useState(0);
  const [step, setStep] = useState("loading"); // loading | idle | playing | pre-recording | recording | review | complete | error
  const [chunks, setChunks] = useState([]);
  const [progress, setProgress] = useState(0);
  const [micLevel, setMicLevel] = useState(0);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isMicAvailable, setIsMicAvailable] = useState(null);
  const [preRecordingTimer, setPreRecordingTimer] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [feedData, setFeedData] = useState(null);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const recordingTimerRef = useRef(null);
  const [replayCount, setReplayCount] = useState(0);

  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const analyserRef = useRef(null);
  const waveformCanvasRef = useRef(null);
  const waveAnimationFrameRef = useRef(null);
  const [analyserNode, setAnalyserNode] = useState(null);
  const audioContextRef = useRef(null);
  const levelIntervalRef = useRef(null);
  const playbackRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  // Playback rate constant: 1 = normal speed; lower values slow down speech
  const SPEECH_PLAYBACK_RATE = 0.8;
  // Draw waveform when recording
  useEffect(() => {
    console.log(
      "🔊 Waveform effect running. step=",
      step,
      "analyserNode=",
      analyserNode,
      "canvasRef=",
      waveformCanvasRef.current,
    );
    if (step !== "recording" || !analyserNode) return;
    const canvas = waveformCanvasRef.current;
    const analyser = analyserNode;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext("2d");
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      analyser.getByteTimeDomainData(dataArray);
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#4979D1";
      ctx.beginPath();

      const sliceWidth = width / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * height) / 2;
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }
      ctx.lineTo(width, height / 2);
      ctx.stroke();

      waveAnimationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (waveAnimationFrameRef.current) {
        cancelAnimationFrame(waveAnimationFrameRef.current);
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [step, analyserNode]);

  // Fetch feed data on mount
  useEffect(() => {
    const fetchFeedData = async () => {
      if (!feedID) {
        setErrorMessage("No feed ID provided");
        setStep("error");
        return;
      }

      try {
        const response = await axios.get(
          `https://genz-staging.feeltiptop.com/api/feed/${feedID}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          },
        );

        if (response.data.success) {
          setFeedData(response.data.data);
          // Extract audio files from assets
          const audioFiles = response.data.data.assets
            .sort((a, b) => a.Position - b.Position)
            .map((asset) => {
              try {
                const content = JSON.parse(asset.AssetContent);
                // Check if this is a SpeechAsset with audio content
                if (
                  content.assetType === "SpeechAsset" &&
                  content.mediaType === "file-audio" &&
                  content.mediaContent
                ) {
                  // Construct the full URL for the audio file
                  return `https://genz-staging.feeltiptop.com/media/assets/${content.mediaContent.replace(/^.*[\\/]/, "")}`;
                }
                return null;
              } catch (error) {
                console.error("Error parsing asset content:", error);
                return null;
              }
            })
            .filter((url) => url); // Remove any null values

          if (audioFiles.length === 0) {
            setErrorMessage("No valid audio files found in this feed");
            setStep("error");
            return;
          }

          setPrompts(audioFiles);
          setStep("idle");
        } else {
          setErrorMessage("Failed to fetch feed data");
          setStep("error");
        }
      } catch (error) {
        console.error("Error fetching feed:", error);
        setErrorMessage("Error loading feed data. Please try again later.");
        setStep("error");
      }
    };

    fetchFeedData();
  }, [feedID]);

  // Reset on prompt change
  useEffect(() => {
    cleanup(); // Call cleanup first
    if (preRecordingTimer) {
      clearTimeout(preRecordingTimer);
    }
    setStep("idle");
    setChunks([]);
    setProgress(0);
    setMicLevel(0);
    setRecordingUrl(null);
    setIsPlaying(false);
    setErrorMessage(null);
    setReplayCount(0); // Reset replay count when prompt changes
  }, [current]);

  // Cleanup recording URL when component unmounts
  useEffect(() => {
    return () => {
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
      }
      // Also clean up media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      // Clean up recorder
      if (recorderRef.current) {
        if (recorderRef.current.state === "recording") {
          recorderRef.current.stop();
        }
      }
      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
      }
      // Clean up level interval
      if (levelIntervalRef.current) {
        clearInterval(levelIntervalRef.current);
      }
      if (preRecordingTimer) {
        clearTimeout(preRecordingTimer);
      }
    };
  }, []);

  // Update play progress
  useEffect(() => {
    const aud = audioRef.current;
    if (step === "playing" && aud) {
      const onTime = () => setProgress((aud.currentTime / aud.duration) * 100);
      aud.addEventListener("timeupdate", onTime);
      return () => aud.removeEventListener("timeupdate", onTime);
    }
  }, [step]);

  // Check if microphone is available
  const checkMicrophoneAvailability = async () => {
    try {
      console.log("Checking microphone availability...");
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput",
      );

      if (audioInputs.length === 0) {
        console.error("No microphone found");
        setIsMicAvailable(false);
        setErrorMessage("No microphone detected on your device.");
        return false;
      }

      // Try to get actual access to confirm permissions
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Stop tracks immediately

      console.log("Microphone is available");
      setIsMicAvailable(true);
      return true;
    } catch (error) {
      console.error("Microphone check failed:", error);
      setIsMicAvailable(false);
      if (error.name === "NotAllowedError") {
        setErrorMessage(
          "Microphone permission denied. Please allow microphone access and reload the page.",
        );
      } else if (error.name === "NotFoundError") {
        setErrorMessage(
          "No microphone found. Please connect a microphone and reload the page.",
        );
      } else {
        setErrorMessage(`Microphone error: ${error.message}`);
      }
      return false;
    }
  };

  const handlePlay = async () => {
    // Check microphone before playing
    if (isMicAvailable === null) {
      const available = await checkMicrophoneAvailability();
      if (!available) {
        return;
      }
    } else if (isMicAvailable === false) {
      alert(
        errorMessage ||
          "Microphone is not available. Please check your settings and reload.",
      );
      return;
    }

    // Apply slowed-down playback rate
    audioRef.current.playbackRate = SPEECH_PLAYBACK_RATE;
    // Play audio if microphone is available
    audioRef.current.play().catch((error) => {
      console.error("Failed to play audio:", error);
      setErrorMessage(`Failed to play audio: ${error.message}`);
      setStep("error");
    });
    setStep("playing");
  };

  const onAudioEnded = () => {
    setStep("pre-recording");
  };

  // Return appropriate mic image based on volume level
  const getMicImage = () => {
    if (micLevel > 0.2) return MicActive;
    if (micLevel > 0.1) return MicSpeaking;
    return MicIdle;
  };

  async function startRecording() {
    try {
      console.log("Starting recording process...");
      setErrorMessage(null);

      // Stop any existing recording first
      if (recorderRef.current) {
        if (recorderRef.current.state === "recording") {
          recorderRef.current.stop();
        }
        recorderRef.current = null;
      }

      // Clean up existing resources
      if (levelIntervalRef.current) {
        clearInterval(levelIntervalRef.current);
        levelIntervalRef.current = null;
      }

      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Reset chunks
      chunksRef.current = [];
      setChunks([]);

      // Clean up previous recording URL
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
        setRecordingUrl(null);
      }

      // Try different mime types in order of preference
      const mimeTypes = [
        "audio/webm",
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];

      const mimeType = mimeTypes.find((type) =>
        MediaRecorder.isTypeSupported(type),
      );

      if (!mimeType) {
        throw new Error(
          "No supported audio format found. Please use Chrome or Firefox.",
        );
      }

      console.log("Using mime type:", mimeType);

      console.log("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 44100,
        },
      });
      console.log("Microphone access granted");

      // Store stream in ref for cleanup
      streamRef.current = stream;

      // Test if the microphone is actually providing sound
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      setAnalyserNode(analyser);

      // Create MediaRecorder instance
      const mr = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      mr.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log("Data available:", event.data.size, "bytes");
          chunksRef.current.push(event.data);
        }
      };

      mr.onstart = () => {
        console.log("MediaRecorder started");
        chunksRef.current = [];
      };

      mr.onstop = async () => {
        console.log("MediaRecorder stopped");
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        const recordedChunks = chunksRef.current;
        console.log("Number of chunks:", recordedChunks.length);

        if (recordedChunks.length > 0) {
          try {
            const blob = new Blob(recordedChunks, { type: mimeType });
            console.log("Created blob of size:", blob.size);

            if (blob.size < 100) {
              throw new Error(
                "Recording too small, likely no audio captured. Please try again.",
              );
            }

            // Create a promise to check if the audio is valid
            const isValidAudio = await new Promise((resolve) => {
              const audio = new Audio();
              audio.onloadedmetadata = () => {
                console.log("Audio duration:", audio.duration, "seconds");
                if (audio.duration < 0.1) {
                  console.error("Audio duration too short:", audio.duration);
                  resolve(false);
                } else {
                  resolve(true);
                }
              };
              audio.onerror = (e) => {
                console.error("Audio loading error:", e);
                resolve(false);
              };
              audio.src = URL.createObjectURL(blob);
            });

            if (isValidAudio) {
              const url = URL.createObjectURL(blob);
              setRecordingUrl(url);
              setChunks(recordedChunks);
              console.log("Audio recording successful");
              setStep("review");
              // Wait for state updates to complete
              await new Promise((resolve) => setTimeout(resolve, 100));
              // Now submit the recording
              await submitRecording(recordedChunks);
            } else {
              throw new Error(
                "Failed to create valid audio. No sound was detected.",
              );
            }
          } catch (error) {
            console.error("Error processing audio:", error);
            setErrorMessage(`Failed to process recording: ${error.message}`);
            setStep("error");
          }
        } else {
          console.error("No audio data was recorded");
          setErrorMessage(
            "No audio was recorded. Please check your microphone settings and try again.",
          );
          setStep("error");
        }
      };

      mr.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        setErrorMessage(`Recording error: ${event.error.message}`);
        setStep("error");
      };

      recorderRef.current = mr;

      // Set up audio level monitoring
      const dataArray = new Uint8Array(analyser.fftSize);

      // Test microphone silence for 500ms before starting
      let silenceCounter = 0;
      const silenceCheck = setInterval(() => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const norm = dataArray[i] / 128 - 1;
          sum += norm * norm;
        }
        const rms = Math.sqrt(sum / dataArray.length);

        if (rms < 0.01) {
          silenceCounter++;
          if (silenceCounter >= 5) {
            // About 500ms of silence
            clearInterval(silenceCheck);
            console.warn(
              "Microphone appears to be silent. Check connections and volume.",
            );
            // Continue anyway, but log the warning
            startActualRecording();
          }
        } else {
          // We detected sound, so proceed with recording
          clearInterval(silenceCheck);
          startActualRecording();
        }
      }, 100);

      // Function to start the actual recording
      function startActualRecording() {
        console.log("Starting MediaRecorder...");

        // Check if there's an existing recording session and clean it up
        if (recorderRef.current && recorderRef.current.state === "recording") {
          console.log("Stopping existing recording session");
          recorderRef.current.stop();
          clearInterval(levelIntervalRef.current);
        }

        // Set up continuous level monitoring
        levelIntervalRef.current = setInterval(() => {
          analyser.getByteTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const norm = dataArray[i] / 128 - 1;
            sum += norm * norm;
          }
          const rms = Math.sqrt(sum / dataArray.length);
          setMicLevel(rms);
        }, 100);

        try {
          // Only start if we're not already recording
          if (
            recorderRef.current &&
            recorderRef.current.state !== "recording"
          ) {
            recorderRef.current.start(100);
          } else {
            console.warn(
              "MediaRecorder is already recording or in an invalid state",
            );
          }
        } catch (err) {
          console.error("Failed to start MediaRecorder:", err);
          setErrorMessage(`Failed to start recording: ${err.message}`);
          setStep("error");

          // Clean up resources
          clearInterval(levelIntervalRef.current);
          if (audioContextRef.current) {
            audioContextRef.current.close().catch(console.error);
          }
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }
        }
      }

      // Set a timeout to prevent hanging if the mic check takes too long
      setTimeout(() => {
        clearInterval(silenceCheck);
        if (recorderRef.current && recorderRef.current.state !== "recording") {
          startActualRecording();
        }
      }, 2000);
    } catch (err) {
      console.error("Recording setup failed:", err);
      setErrorMessage(`Failed to start recording: ${err.message}`);
      setStep("error");
      await cleanup();
    }
  }

  const stopRecording = () => {
    console.log("Stopping recording...");
    try {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      } else {
        console.warn(
          "MediaRecorder is not recording",
          recorderRef.current?.state,
        );
        setErrorMessage(
          "Recording was not properly started. Please try again.",
        );
        setStep("error");
      }
    } catch (err) {
      console.error("Error stopping recording:", err);
      setErrorMessage(`Error stopping recording: ${err.message}`);
      setStep("error");
    } finally {
      clearInterval(levelIntervalRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
      }
      setMicLevel(0);
    }
  };

  const retryRecording = () => {
    setStep("idle");
    setErrorMessage(null);
    // Don't increment current prompt index when retrying
  };

  const submitRecording = async (chunksToSubmit) => {
    try {
      console.log("Submit Recording called");
      console.log("Chunks to submit:", chunksToSubmit?.length);

      // Check if we have valid recording data
      if (!chunksToSubmit || chunksToSubmit.length === 0) {
        console.error("No recording data available");
        setErrorMessage("No recording data available. Please try again.");
        setStep("error");
        return;
      }

      // Create the audio file
      const audioBlob = new Blob(chunksToSubmit, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.wav");

      try {
        // Save the recording file
        const saveResponse = await axios.post(
          "https://genz-staging.feeltiptop.com/api/save-recording",
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        if (!saveResponse.data.success) {
          throw new Error("Failed to save recording");
        }

        const mediaPath = saveResponse.data.file_path;

        // Save this recording
        const newRecording = {
          assetID: feedData.assets[current].AssetID,
          recordingUrl: mediaPath,
        };

        // Check if this is the last recording
        if (current + 1 >= prompts.length) {
          console.log("This is the last recording, sending all recordings");
          const allRecordings = [...recordings, newRecording];
          const payload = {
            recordings: allRecordings,
            feedID: feedID,
          };
          if (patientID) payload.patientID = patientID;
          const response = await axios.post(
            "https://genz-staging.feeltiptop.com/api/save-recordings",
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "Content-Type": "application/json",
              },
            },
          );

          if (response.data.success) {
            console.log("All recordings saved and processed successfully");
            setStep("complete");
          } else {
            setErrorMessage("Failed to save and process recordings");
            setStep("error");
          }
        } else {
          console.log("Moving to next recording");
          setRecordings((prev) => [...prev, newRecording]);
          setCurrent((ci) => ci + 1);
          setStep("idle");
        }
      } catch (error) {
        console.error("Error in submission:", error);
        setErrorMessage(`Failed to submit recording: ${error.message}`);
        setStep("error");
      }
    } catch (err) {
      console.error("Error in submitRecording:", err);
      setErrorMessage(`Failed to process recording: ${err.message}`);
      setStep("error");
    }
  };

  const playRecording = () => {
    if (recordingUrl && !isPlaying) {
      if (playbackRef.current) {
        playbackRef.current.pause();
        playbackRef.current = null;
      }

      const audio = new Audio(recordingUrl);
      audio.onended = () => {
        setIsPlaying(false);
        playbackRef.current = null;
      };
      audio.onerror = (e) => {
        console.error("Playback error:", e);
        setIsPlaying(false);
        playbackRef.current = null;
        setErrorMessage("Failed to play recording. The file may be corrupted.");
      };

      playbackRef.current = audio;
      audio.play().catch((err) => {
        console.error("Playback failed:", err);
        setIsPlaying(false);
        setErrorMessage(`Playback failed: ${err.message}`);
      });
      setIsPlaying(true);
    }
  };

  const stopPlayback = () => {
    if (playbackRef.current && isPlaying) {
      playbackRef.current.pause();
      playbackRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const getProgressImage = () => {
    if (progress >= 100) return ProgressComplete;
    if (progress >= 75) return ProgressFull;
    if (progress >= 25) return ProgressHalf;
    return ProgressZero;
  };

  const cleanup = async () => {
    // Clean up recorder
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    recorderRef.current = null;

    // Clean up intervals
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current);
      levelIntervalRef.current = null;
    }

    // Clean up audio context - only if it's not already closed
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        await audioContextRef.current.close();
      } catch (err) {
        console.error("Error closing audio context:", err);
      }
    }
    audioContextRef.current = null;

    // Clean up media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Clean up recording URL
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }

    setMicLevel(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingProgress(0);
  };

  if (step === "complete") return <CompletedScreen />;
  if (step === "loading")
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-xl">Loading audio files...</div>
          {errorMessage && <div className="text-red-600">{errorMessage}</div>}
        </div>
      </div>
    );

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-white to-blue-50 p-4">
      <header className="absolute left-6 top-6">
        <img src={Logo} alt="COMMA logo" className="h-8 w-auto" />
      </header>
      <audio ref={audioRef} src={prompts[current]} onEnded={onAudioEnded} />

      {step === "idle" && (
        <div className="flex flex-col items-center">
          {isMicAvailable === false && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-800">
              <p className="font-medium">Microphone Error</p>
              <p>{errorMessage}</p>
            </div>
          )}
          <button
            onClick={handlePlay}
            className="h-80 w-80"
            disabled={isMicAvailable === false}
          >
            <img src={ProgressZero} alt="Start" className="h-full w-full" />
          </button>
          {/* <p className="mt-4 text-gray-700">
            {isMicAvailable === null
              ? "Checking microphone availability..."
              : isMicAvailable === true
                ? "Click to start"
                : "Please check your microphone settings"}
          </p> */}
        </div>
      )}
      {step === "playing" && (
        <div className="h-80 w-80">
          {progress < 100 ? (
            /* animate until full */
            <PlayButton onClick={() => {}} progress={progress} />
          ) : (
            /* 100 % reached → show "complete" frame (Frame 38) */
            <img
              src={ProgressComplete} // your Frame 38 image
              alt="Progress complete"
              className="h-full w-full"
            />
          )}
        </div>
      )}

      {step === "pre-recording" && (
        <div className="flex flex-col items-center">
          <img src={getMicImage()} alt="Mic State" className="mb-8 h-56 w-56" />
          <canvas
            ref={waveformCanvasRef}
            width={448}
            height={80}
            className=""
            style={{
              position: "relative",
              top: "2rem",
              width: "448px",
              height: "80px",
              backgroundColor: "#F0F4FF",
            }}
          />
          <div className="mt-8 flex gap-4">
            <button
              onClick={() => {
                handlePlay();
                setReplayCount((prev) => prev + 1);
              }}
              className="rounded-lg bg-blue-500 px-6 py-2 text-white hover:bg-blue-600"
              disabled={replayCount >= 3}
            >
              Replay Audio
            </button>
            <button
              onClick={() => {
                setStep("recording");
                startRecording();
                // Get the duration of the current audio file
                const audioDuration =
                  audioRef.current.duration / SPEECH_PLAYBACK_RATE;
                const recordingDuration = (audioDuration + 2) * 1000; // Convert to milliseconds

                // Set up progress updates
                const startTime = Date.now();
                recordingTimerRef.current = setInterval(() => {
                  const elapsed = Date.now() - startTime;
                  const progress = (elapsed / recordingDuration) * 100;
                  setRecordingProgress(Math.min(progress, 100));
                }, 100);

                // Set timer to stop recording after audio duration + 2 seconds
                setTimeout(() => {
                  if (recordingTimerRef.current) {
                    clearInterval(recordingTimerRef.current);
                  }
                  stopRecording();
                }, recordingDuration);
              }}
              className="rounded-lg bg-green-500 px-6 py-2 text-white hover:bg-green-600"
            >
              Start Recording
            </button>
          </div>
          {replayCount >= 3 && (
            <div className="mt-2 text-red-600">You have reached the maximum number of replays for this prompt.</div>
          )}
        </div>
      )}
      {step === "recording" && (
        <div className="flex flex-col items-center">
          <img src={getMicImage()} alt="Mic State" className="mb-8 h-56 w-56" />
          <Box sx={{ width: "300px", mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={recordingProgress}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#2196f3",
                  borderRadius: 5,
                },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 1 }}
            >
              {Math.round(100 - recordingProgress)}% time remaining
            </Typography>
          </Box>
          <canvas
            ref={waveformCanvasRef}
            width={448}
            height={80}
            className=""
            style={{
              position: "relative",
              top: "2rem",
              width: "448px",
              height: "80px",
              backgroundColor: "#F0F4FF",
            }}
          />
          {/* <p className="mb-4 text-center text-gray-700">
            You'll move to the next task as soon as you finish speaking.
          </p> */}
        </div>
      )}
      {step === "review" && (
        <div className="flex flex-col items-center">
          <p className="mb-4 text-gray-700">Moving to next task...</p>
          {errorMessage && (
            <div className="mt-4 text-red-500">{errorMessage}</div>
          )}
        </div>
      )}
      {step === "error" && (
        <div className="flex flex-col items-center">
          <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-800">
            <p className="font-medium">Error</p>
            <p>{errorMessage}</p>
          </div>
          <button
            onClick={retryRecording}
            className="rounded-lg bg-blue-500 px-8 py-3 text-white hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Browser support debugging info */}
      <div className="absolute bottom-2 right-2 text-xs text-gray-400">
        {navigator.mediaDevices && MediaRecorder
          ? `Supported: ${MediaRecorder.isTypeSupported("audio/webm") ? "webm" : ""} 
             ${MediaRecorder.isTypeSupported("audio/ogg") ? "ogg" : ""}`
          : "MediaRecorder not supported"}
      </div>
    </div>
  );
}
